import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Card, 
  Table, 
  Button, 
  Input, 
  Space, 
  Typography, 
  Spin, 
  Alert, 
  Tabs, 
  Tag,
  Modal,
  Tooltip,
  Select
} from 'antd';
import { 
  ReloadOutlined, 
  LinkOutlined, 
  FileOutlined, 
  DatabaseOutlined,
  CloudOutlined,
  EyeOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import fetchRDFoxData from '../api/RDFoxRequest';
import { checkServerAvailability } from '../utils/rdfoxUtils';
import { listS3Objects, getSignedUrl, getJsonFromS3, getCsvFromS3, S3ObjectItem } from '../utils/s3Utils';

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

// Interface pour les métadonnées RDFox
interface Metadata {
  id: string;
  name: string;
  description: string;
  s3Path: string;
  dataType: string;
  lastModified: string;
  tags: string[];
}

// Interface pour les objets S3
interface S3Object {
  key: string;
  size: number;
  lastModified: Date;
  displayName: string;
}

// Fonction pour formater la taille des fichiers
const formatBytes = (bytes: number, decimals: number = 2): string => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
};

const IntegratedExplorer: React.FC = () => {
  // État pour RDFox
  const [serverStatus, setServerStatus] = useState<{
    available: boolean;
    datastores: string[];
  }>({ available: false, datastores: [] });
  
  const [selectedDatastore, setSelectedDatastore] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [metadata, setMetadata] = useState<Metadata[]>([]);
  
  // État pour S3
  const [s3Objects, setS3Objects] = useState<S3Object[]>([]);
  const [s3Loading, setS3Loading] = useState<boolean>(false);
  const [s3Error, setS3Error] = useState<string | null>(null);
  
  // État pour la recherche
  const [searchText, setSearchText] = useState<string>('');
  
  // État pour la prévisualisation
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewTitle, setPreviewTitle] = useState<string>('');
  const [previewContent, setPreviewContent] = useState<any>(null);
  const [previewLoading, setPreviewLoading] = useState<boolean>(false);
  const [previewType, setPreviewType] = useState<'json' | 'csv' | 'text' | 'image' | 'unknown'>('unknown');
  
  // État pour l'élément sélectionné
  const [selectedItem, setSelectedItem] = useState<Metadata | null>(null);
  
  // Charger les informations RDFox au chargement de la page
  useEffect(() => {
    checkRDFoxServer();
  }, []);
  
  // Charger les métadonnées lorsque le datastore est sélectionné
  useEffect(() => {
    if (selectedDatastore) {
      fetchMetadata();
    }
  }, [selectedDatastore]);
  
  // Fonction pour vérifier le serveur RDFox
  const checkRDFoxServer = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Checking RDFox server');
      
      // Vérifier la disponibilité du serveur
      const status = await checkServerAvailability();
      console.log('Check result:', status);
      setServerStatus(status);
      
      if (status.available && status.datastores.length > 0) {
        // Sélectionner le premier datastore
        setSelectedDatastore(status.datastores[0]);
      } else {
        setError('Aucun datastore disponible sur le serveur RDFox');
      }
    } catch (err) {
      console.error('Error checking RDFox server:', err);
      setError(`Failed to connect to RDFox server: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour récupérer les métadonnées depuis RDFox
  const fetchMetadata = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Requête SPARQL pour récupérer les métadonnées
      const query = `
        PREFIX rdf: <http://www.w3.org/1999/02/22-rdf-syntax-ns#>
        PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>
        PREFIX xsd: <http://www.w3.org/2001/XMLSchema#>
        PREFIX : <http://example.org/ontology/>
        
        SELECT ?id ?name ?description ?s3Path ?dataType ?lastModified ?tags
        WHERE {
          ?id a :Dataset ;
              rdfs:label ?name .
          OPTIONAL { ?id rdfs:comment ?description }
          OPTIONAL { ?id :s3Path ?s3Path }
          OPTIONAL { ?id :dataType ?dataType }
          OPTIONAL { ?id :lastModified ?lastModified }
          OPTIONAL { ?id :hasTag ?tagNode . ?tagNode rdfs:label ?tag }
        }
        ORDER BY ?name
      `;
      
      console.log('Executing SPARQL query:', query);
      
      // Exécuter la requête
      const response = await fetchRDFoxData(selectedDatastore, query);
      console.log('SPARQL response:', response);
      
      if (!response?.results?.bindings) {
        console.warn('No data available in the response');
        setMetadata([]);
        return;
      }
      
      // Transformer les résultats
      const metadataMap = new Map<string, Metadata>();
      
      response.results.bindings.forEach((row: any) => {
        const id = row.id?.value || '';
        
        if (!metadataMap.has(id)) {
          metadataMap.set(id, {
            id,
            name: row.name?.value || 'Sans nom',
            description: row.description?.value || 'Aucune description',
            s3Path: row.s3Path?.value || '',
            dataType: row.dataType?.value || 'Inconnu',
            lastModified: row.lastModified?.value || '',
            tags: row.tag?.value ? [row.tag.value] : []
          });
        } else if (row.tag?.value) {
          // Ajouter le tag s'il existe
          const metadata = metadataMap.get(id)!;
          if (!metadata.tags.includes(row.tag.value)) {
            metadata.tags.push(row.tag.value);
          }
        }
      });
      
      // Convertir la map en tableau
      const metadataArray = Array.from(metadataMap.values());
      console.log('Parsed metadata:', metadataArray);
      
      setMetadata(metadataArray);
      
      // Si des chemins S3 sont présents, charger les objets S3
      const s3Paths = metadataArray
        .map(item => item.s3Path)
        .filter(Boolean);
      
      if (s3Paths.length > 0) {
        fetchS3Objects();
      }
    } catch (err) {
      console.error('Error fetching metadata:', err);
      setError(`Failed to fetch metadata: ${err instanceof Error ? err.message : String(err)}`);
      setMetadata([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Fonction pour charger les objets S3
  const fetchS3Objects = async () => {
    try {
      setS3Loading(true);
      setS3Error(null);
      
      // Récupérer les objets S3 (utiliser 'dataspace1' comme bucket par défaut)
      const objects = await listS3Objects('dataspace1', '');
      
      // Transformer les objets S3
      const transformedObjects: S3Object[] = objects.map((object: S3ObjectItem) => ({
        key: object.Key || '',
        size: object.Size || 0,
        lastModified: object.LastModified || new Date(),
        displayName: object.Key ? object.Key.split('/').pop() || object.Key : ''
      }));
      
      // Trier les objets par nom
      const sortedObjects = transformedObjects.sort((a, b) => 
        a.displayName.localeCompare(b.displayName)
      );
      
      setS3Objects(sortedObjects);
    } catch (err) {
      console.error('Error fetching S3 objects:', err);
      setS3Error(`Failed to load S3 objects: ${err instanceof Error ? err.message : String(err)}`);
      setS3Objects([]);
    } finally {
      setS3Loading(false);
    }
  };
  
  // Fonction pour télécharger un fichier S3
  const downloadFile = async (fileKey: string, fileName: string) => {
    try {
      // Obtenir l'URL signée
      const signedUrl = await getSignedUrl(fileKey);
      
      // Créer un lien temporaire pour le téléchargement
      const link = document.createElement('a');
      link.href = signedUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading file:', err);
      setError(`Failed to download file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
  
  // Fonction pour prévisualiser un fichier
  const previewFile = async (fileKey: string, fileName: string) => {
    try {
      setPreviewLoading(true);
      setPreviewVisible(true);
      setPreviewTitle(fileName);
      setPreviewContent(null);
      
      // Déterminer le type de fichier
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      
      if (['jpg', 'jpeg', 'png', 'gif'].includes(extension)) {
        // Image
        setPreviewType('image');
        setPreviewContent(await getSignedUrl(fileKey));
      } else if (extension === 'json') {
        // JSON
        setPreviewType('json');
        const jsonData = await getJsonFromS3(fileKey);
        setPreviewContent(jsonData);
      } else if (extension === 'csv') {
        // CSV
        setPreviewType('csv');
        const csvData = await getCsvFromS3(fileKey);
        setPreviewContent(csvData);
      } else {
        // Texte ou inconnu
        setPreviewType('text');
        const signedUrl = await getSignedUrl(fileKey);
        const response = await fetch(signedUrl);
        const text = await response.text();
        setPreviewContent(text);
      }
    } catch (err) {
      console.error('Error previewing file:', err);
      setPreviewContent(`Failed to preview file: ${err instanceof Error ? err.message : String(err)}`);
      setPreviewType('text');
    } finally {
      setPreviewLoading(false);
    }
  };
  
  // Fonction pour fermer la prévisualisation
  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewTitle('');
    setPreviewContent(null);
    setPreviewType('unknown');
  };
  
  // Fonction pour afficher les détails d'un élément
  const showItemDetails = (item: Metadata) => {
    setSelectedItem(item);
    
    // Si l'élément a un chemin S3, prévisualiser le fichier
    if (item.s3Path) {
      const fileName = item.s3Path.split('/').pop() || item.s3Path;
      previewFile(item.s3Path, fileName);
    }
  };
  
  // Filtrer les métadonnées en fonction de la recherche
  const filteredMetadata = metadata.filter(item => 
    item.name.toLowerCase().includes(searchText.toLowerCase()) ||
    item.description.toLowerCase().includes(searchText.toLowerCase()) ||
    item.tags.some(tag => tag.toLowerCase().includes(searchText.toLowerCase()))
  );
  
  // Colonnes pour le tableau des métadonnées
  const metadataColumns = [
    {
      title: 'Nom',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Metadata) => (
        <a onClick={() => showItemDetails(record)}>{text}</a>
      ),
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: 'Type de données',
      dataIndex: 'dataType',
      key: 'dataType',
    },
    {
      title: 'Tags',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) => (
        <>
          {tags.map(tag => (
            <Tag color="blue" key={tag}>
              {tag}
            </Tag>
          ))}
        </>
      ),
    },
    {
      title: 'Fichier S3',
      dataIndex: 's3Path',
      key: 's3Path',
      render: (path: string) => path ? (
        <Tooltip title={path}>
          <LinkOutlined /> {path.split('/').pop() || path}
        </Tooltip>
      ) : (
        <Text type="secondary">Aucun fichier lié</Text>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Metadata) => (
        <Space>
          {record.s3Path && (
            <>
              <Tooltip title="Télécharger">
                <Button
                  icon={<DownloadOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadFile(record.s3Path, record.s3Path.split('/').pop() || record.s3Path);
                  }}
                />
              </Tooltip>
              <Tooltip title="Prévisualiser">
                <Button
                  icon={<EyeOutlined />}
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    const fileName = record.s3Path.split('/').pop() || record.s3Path;
                    previewFile(record.s3Path, fileName);
                  }}
                />
              </Tooltip>
            </>
          )}
        </Space>
      ),
    },
  ];
  
  // Colonnes pour le tableau des objets S3
  const s3Columns = [
    {
      title: 'Nom',
      dataIndex: 'displayName',
      key: 'displayName',
      render: (text: string, record: S3Object) => (
        <Space>
          <FileOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: 'Taille',
      dataIndex: 'size',
      key: 'size',
      render: (size: number) => formatBytes(size),
    },
    {
      title: 'Dernière modification',
      dataIndex: 'lastModified',
      key: 'lastModified',
      render: (date: Date) => date.toLocaleString(),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: S3Object) => (
        <Space>
          <Tooltip title="Télécharger">
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => downloadFile(record.key, record.displayName)}
            />
          </Tooltip>
          <Tooltip title="Prévisualiser">
            <Button
              icon={<EyeOutlined />}
              size="small"
              onClick={() => previewFile(record.key, record.displayName)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];
  
  // Rendu du contenu de la prévisualisation
  const renderPreviewContent = () => {
    if (previewLoading) {
      return <Spin tip="Chargement de la prévisualisation..." />;
    }
    
    switch (previewType) {
      case 'json':
        return (
          <pre style={{ maxHeight: '500px', overflow: 'auto' }}>
            {JSON.stringify(previewContent, null, 2)}
          </pre>
        );
      case 'csv':
        // Afficher les données CSV sous forme de tableau
        if (Array.isArray(previewContent) && previewContent.length > 0) {
          const columns = Object.keys(previewContent[0]).map(key => ({
            title: key,
            dataIndex: key,
            key: key,
          }));
          
          return (
            <Table
              dataSource={previewContent.map((item, index) => ({ ...item, key: index }))}
              columns={columns}
              pagination={{ pageSize: 10 }}
              scroll={{ x: 'max-content' }}
            />
          );
        }
        return <Text>Aucune donnée à afficher</Text>;
      case 'image':
        return <img src={previewContent} alt={previewTitle} style={{ maxWidth: '100%' }} />;
      case 'text':
        return (
          <pre style={{ maxHeight: '500px', overflow: 'auto', whiteSpace: 'pre-wrap' }}>
            {previewContent}
          </pre>
        );
      default:
        return <Text>Prévisualisation non disponible pour ce type de fichier</Text>;
    }
  };
  
  return (
    <Content style={{ padding: '0 24px', minHeight: 280 }}>
      <Card title={<Title level={4}>Explorateur Intégré RDFox + S3</Title>} style={{ marginTop: 24 }}>
        <Paragraph>
          Cet explorateur montre la relation entre les métadonnées stockées dans RDFox et les fichiers stockés dans S3.
          Les métadonnées contiennent des références aux fichiers S3, permettant de lier les deux systèmes.
        </Paragraph>
        
        {/* Barre d'outils */}
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                checkRDFoxServer();
                fetchS3Objects();
              }}
              loading={loading || s3Loading}
            >
              Actualiser tout
            </Button>
            
            <Select
              placeholder="Sélectionner un datastore"
              value={selectedDatastore}
              onChange={setSelectedDatastore}
              style={{ width: 200 }}
              disabled={loading || !serverStatus.available}
            >
              {serverStatus.datastores.map(datastore => (
                <Option key={datastore} value={datastore}>{datastore}</Option>
              ))}
            </Select>
            
            <Input
              placeholder="Rechercher..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ width: 200 }}
              allowClear
            />
          </Space>
        </div>
        
        {/* Messages d'erreur */}
        {error && (
          <Alert
            message="Erreur RDFox"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {s3Error && (
          <Alert
            message="Erreur S3"
            description={s3Error}
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        
        {/* Onglets pour les différentes vues */}
        <Tabs defaultActiveKey="integrated">
          <TabPane tab={<span><LinkOutlined /> Vue intégrée</span>} key="integrated">
            <Table
              dataSource={filteredMetadata.map(item => ({ ...item, key: item.id }))}
              columns={metadataColumns}
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'Aucune métadonnée trouvée' }}
            />
          </TabPane>
          
          <TabPane tab={<span><DatabaseOutlined /> Métadonnées RDFox</span>} key="rdfox">
            <Table
              dataSource={filteredMetadata.map(item => ({ ...item, key: item.id }))}
              columns={metadataColumns.filter(col => col.key !== 'actions')}
              loading={loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'Aucune métadonnée trouvée' }}
            />
          </TabPane>
          
          <TabPane tab={<span><CloudOutlined /> Fichiers S3</span>} key="s3">
            <Table
              dataSource={s3Objects.map(obj => ({ ...obj, key: obj.key }))}
              columns={s3Columns}
              loading={s3Loading}
              pagination={{ pageSize: 10 }}
              locale={{ emptyText: 'Aucun fichier trouvé' }}
            />
          </TabPane>
        </Tabs>
      </Card>
      
      {/* Modal de prévisualisation */}
      <Modal
        title={previewTitle}
        visible={previewVisible}
        onCancel={closePreview}
        footer={[
          <Button key="close" onClick={closePreview}>
            Fermer
          </Button>,
          <Button
            key="download"
            type="primary"
            icon={<DownloadOutlined />}
            onClick={() => {
              if (selectedItem?.s3Path) {
                downloadFile(selectedItem.s3Path, selectedItem.s3Path.split('/').pop() || selectedItem.s3Path);
              } else {
                const fileKey = s3Objects.find(obj => obj.displayName === previewTitle)?.key;
                if (fileKey) {
                  downloadFile(fileKey, previewTitle);
                }
              }
            }}
          >
            Télécharger
          </Button>,
        ]}
        width={800}
      >
        {renderPreviewContent()}
      </Modal>
    </Content>
  );
};

export default IntegratedExplorer;
